import { config } from "../config";
import { callGateway, type CallerIdentity } from "./gateway";
import { isMobile10 } from "../domain/testMobile";

/**
 * The sub-operators an operator adds beneath itself -- radaccess
 * /access/operator/{ns}/suboperators (feat/suboperator, owner mandate
 * 2026-09-19), on the RBAC resource `suboperator` (superadmin and Operator).
 *
 * 🔑 {ns} is the PARENT. The gateway answers only for the caller's own
 * namespace or a descendant, and suspend/resume act only on a child the parent
 * itself created.
 */
export type SubOperatorKind = "site" | "franchise" | "licensed" | "pin";

export const SUB_OPERATOR_KINDS: readonly { kind: SubOperatorKind; label: string; help: string }[] = [
  { kind: "site", label: "New site of mine", help: "Your own second location. Same business, your plans, no invoice between you." },
  { kind: "franchise", label: "Franchise", help: "Sells under your licence and your tariff. Invoices as your branch." },
  { kind: "licensed", label: "Independent licensed operator", help: "Holds its own licence, sets its own tariff, invoices in its own name." },
  { kind: "pin", label: "Unlicensed operator (PIN)", help: "No licence. Works on PINs bought from you; the clients it selects get a tax invoice in your name." },
];

/**
 * The kinds a parent of this type may add -- radaccess SubOperatorKindAllowed.
 * A Network Operator or Cloud Tenant adds sites only; a PIN provider only under
 * a licensed ISP or VNO. Unknown type: all, and radaccess decides.
 */
export function subOperatorKindsFor(parentType: string | null): typeof SUB_OPERATOR_KINDS {
  if (parentType === null || parentType === "") return SUB_OPERATOR_KINDS;
  if (parentType === "NETWORK_OPERATOR" || parentType === "CLOUD_TENANT") return SUB_OPERATOR_KINDS.filter((k) => k.kind === "site");
  if (parentType === "ISP" || parentType === "VNO") return SUB_OPERATOR_KINDS;
  return SUB_OPERATOR_KINDS.filter((k) => k.kind !== "pin");
}

export const LICENSE_TYPES = ["UL", "ISP_A", "ISP_B", "ISP_C", "VNO"] as const;

export interface SubOperator {
  namespace: string;
  parent: string;
  kind: SubOperatorKind;
  legalname: string;
  operatortype: string;
  active: boolean;
  holdslicense: boolean;
  subscriberinvoiceidentity: string;
  suspended: boolean;
  suspendedat?: string;
  suspensionreason?: string;
  createdby: string;
  createdat: string;
}

export interface SubOperatorInput {
  label: string;
  kind: SubOperatorKind;
  legalname: string;
  operatortype?: string;
  mobile: string;
  email?: string;
  licensetype?: string;
  licensenumber?: string;
}

const base = (parent: string) => `/operator/${encodeURIComponent(parent)}/suboperators`;

export async function listSubOperators(identity: CallerIdentity, parent: string, signal?: AbortSignal): Promise<SubOperator[]> {
  const r = await callGateway<SubOperator[] | null>(identity, config().gatewayBase, base(parent), signal ? { signal } : {});
  return r.data ?? [];
}

export async function createSubOperator(identity: CallerIdentity, parent: string, input: SubOperatorInput): Promise<SubOperator> {
  const r = await callGateway<SubOperator>(identity, config().gatewayBase, base(parent), { method: "POST", body: cleanInput(input) });
  return r.data;
}

export async function setSubOperatorSuspended(
  identity: CallerIdentity,
  parent: string,
  label: string,
  suspend: boolean,
  reason = "",
): Promise<void> {
  const path = `${base(parent)}/${encodeURIComponent(label)}/${suspend ? "suspend" : "resume"}`;
  await callGateway<unknown>(identity, config().gatewayBase, path, { method: "PUT", ...(suspend && reason.trim() ? { body: { reason: reason.trim() } } : {}) });
}

/**
 * The body sent: licence fields only for a licensed operator, blanks dropped.
 * radaccess refuses unknown fields, so nothing else may ride along.
 */
export function cleanInput(i: SubOperatorInput): SubOperatorInput {
  const out: SubOperatorInput = {
    label: i.label.trim().toLowerCase(),
    kind: i.kind,
    legalname: i.legalname.trim(),
    mobile: i.mobile.trim(),
  };
  if (i.email?.trim()) out.email = i.email.trim();
  if (i.operatortype?.trim()) out.operatortype = i.operatortype.trim();
  if (i.kind === "licensed") {
    if (i.licensetype?.trim()) out.licensetype = i.licensetype.trim();
    if (i.licensenumber?.trim()) out.licensenumber = i.licensenumber.trim();
  }
  return out;
}

/** The label rule radaccess enforces, checked before sending. */
export function isValidLabel(label: string): boolean {
  return /^[a-z0-9]([a-z0-9-]{0,30}[a-z0-9])?$/.test(label.trim().toLowerCase());
}

/** The mobile rule radaccess enforces: ten digits starting 6-9, +91 allowed. */
export function isValidIndianMobile(mobile: string): boolean {
  let d = mobile.replace(/[\s-]/g, "").replace(/^\+/, "");
  if (d.length === 12 && d.startsWith("91")) d = d.slice(2);
  return isMobile10(d);
}

/** The last segment: what suspend/resume address a child by. */
export function labelOf(namespace: string): string {
  return namespace.slice(namespace.lastIndexOf(".") + 1);
}

export function kindLabel(kind: string): string {
  return SUB_OPERATOR_KINDS.find((k) => k.kind === kind)?.label ?? kind;
}
